import React, { createContext, useState, useEffect, useCallback } from 'react';
import { tokenService } from '../services/auth/token.service';
import { oauth2Service } from '../services/auth/oauth2.service';
import { UserProfile, SocialProvider } from '../types/auth.types';

interface AuthContextType {
  user: UserProfile | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (provider?: SocialProvider, returnTo?: string) => Promise<void>;
  loginWithCredentials: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  logout: () => void;
  handleCallback: (code: string, state: string) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(tokenService.getUser());
  const [token, setToken] = useState<string | null>(tokenService.getAccessToken());
  const [isLoading, setIsLoading] = useState(true);

  const logout = useCallback(async () => {
    await oauth2Service.logout();
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = tokenService.getAccessToken();
      const storedUser = tokenService.getUser();

      if (storedToken) {
        setToken(storedToken);
        if (storedUser) {
          setUser(storedUser);
        } else {
          try {
            const userData = await oauth2Service.getUserInfo();
            setUser(userData);
            tokenService.setUser(userData);
          } catch (error) {
            console.error('Failed to fetch user info during auth check', error);
            // Only logout on 401 (invalid/expired token), not on network or other errors
            if (error && typeof error === 'object' && 'response' in error &&
                (error as any).response?.status === 401) {
              await logout();
            }
            // For other errors (network, CORS, 5xx), keep the session alive
            // The user can still use the app with the token
          }
        }
      }
      setIsLoading(false);
    };
    checkAuth();
  }, [logout]);

  const login = useCallback(async (provider?: SocialProvider, returnTo?: string) => {
    window.location.href = await oauth2Service.getAuthorizationUrl({ provider, returnTo });
  }, []);

  const loginWithCredentials = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const data = await oauth2Service.authenticateWithCredentials(email, password);
      setToken(data.access_token);
      setUser(data.user);
    } catch (error) {
      console.error('Login with credentials failed', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const register = useCallback(async (name: string, email: string, password: string) => {
    setIsLoading(true);
    try {
      // AuthKeystone spec: Registration returns User object, not tokens
      // User needs to login separately after registration
      await oauth2Service.register({ name, email, password });
      // After successful registration, automatically login the user
      const data = await oauth2Service.authenticateWithCredentials(email, password);
      setToken(data.access_token);
      setUser(data.user);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const forgotPassword = useCallback(async (email: string) => {
    setIsLoading(true);
    try {
      await oauth2Service.forgotPassword(email);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleCallback = useCallback(async (code: string, state: string) => {
    setIsLoading(true);
    try {
      const data = await oauth2Service.exchangeCodeForToken(code, state);
      setToken(data.access_token);

      if (data.user) {
        setUser(data.user);
      } else {
        // AuthKeystone spec: If user not in token response, fetch from userinfo
        try {
          const userData = await oauth2Service.getUserInfo();
          setUser(userData);
          tokenService.setUser(userData);
        } catch (userInfoError: any) {
          console.error('Failed to fetch user info after token exchange', userInfoError);
          // Only treat 401 as fatal (token is invalid); other errors are non-blocking
          if (userInfoError?.response?.status === 401) throw userInfoError;
        }
      }
    } catch (error) {
      console.error('OAuth2 callback failed', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      isAuthenticated: !!token,
      isLoading,
      login,
      loginWithCredentials,
      register,
      forgotPassword,
      logout,
      handleCallback
    }}>
      {children}
    </AuthContext.Provider>
  );
};


